import styled from '@emotion/styled';
import classnames from 'classnames';
import React from 'react';
import { useNavigate } from 'react-router';

import colors from 'config/theme/colors';
import { NOTIFICATION_STATUS } from 'modules/notifications/constants';
import settings, { getEventField } from 'modules/notifications/data/settings';
import StopPropagationClickBase from 'ui/utils/StopPropagationClick';

import { getComponent } from './components/Body';
import NotificationDropdown from './components/DropdownMenu';

const StopPropagationClick = styled(StopPropagationClickBase)`
  display: inline;
`;

const NotificationWrapper = styled.div`
  cursor: pointer;
  color: ${colors['@text-color-secondary']};
  padding: 24px;
  display: flex;
  position: relative;
  color: ${colors['@text-color']};
  font-size: 14px;
  transition: background 200ms;
  padding-left: 34px; 

  a {
    color: ${colors['@primary-color']};
    font-weight: bold;
  }

  &:hover {
    background-color: ${colors['@notification-bg-hover']} !important;
  }

  &.unread {
    background-color: ${colors['@notification-bg']};
  }

  &.unread::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: ${colors['@primary-color']};
    position: absolute;
    left: 20px;
    top: 50%;
    transform: translateY(-50%);
  }
`;

/** @type {React.FC<import('modules/notifications/types').Notification>} */
const Notification = ({ onMenuClose, ...notification }) => {
  const navigate = useNavigate();

  const { status, extension } = notification;
  const isUnread = status === NOTIFICATION_STATUS.UNREAD;

  const Body = getComponent(notification);

  const eventField = getEventField(notification);
  const definition = settings[extension]?.[eventField];
  const { link: path, navigationState } = definition?.ui?.(notification) ?? {};

  const handleOnClick = React.useCallback(() => {
    if (path) {
      navigate(path, { state: navigationState });

      if (typeof onMenuClose === 'function') {
        onMenuClose();
      }
    }
  }, [navigate, onMenuClose, navigationState, path]);

  return (
    <NotificationWrapper
      onClick={handleOnClick}
      data-cy="notification-wrapper"
      className={classnames({ unread: isUnread })}
    >
      <Body {...notification} />
      <StopPropagationClick>
        <NotificationDropdown {...notification} />
      </StopPropagationClick>
    </NotificationWrapper>
  );
};

export default Notification;
